/** Read-only "Stops Summary" info modal — the ONLY thing the Quote Detail metadata card's
 * "Quote Type" cell opens (QuoteDetail/index.tsx owns the open state). Deliberately an INFO modal,
 * never the Edit Quote modal: that card stopped opening QuoteDetailModalNew on purpose (see
 * QuoteDetail/index.tsx's "No onOpenModal" note), and every other cell in the card stays inert.
 *
 * Restored from the original implementation that lived inside CustomerPortalStopMetaStrip.tsx and
 * was removed in ffda8178 ("remove detail modal on CustomerPortalStopMetaStrip"). It lives here
 * instead of back in the strip so that removal stands — the strip renders the strip, this file
 * renders the modal — and so it sits with the module's other modals.
 *
 * Renders with the same StopDetailBlock the Edit Quote modal's "Pickups & Drop-offs" tab uses
 * (MyQuote/Modals/PickupsDropoffsDetails.tsx), so both surfaces show identical stop information
 * rather than a second, drifting implementation. Pixel-matches Figma WAL-HQ-V2 Quoting node
 * 71696:503245 (numbered address block, Stop Pickups / Stop Dropoffs tables, Pickup/Delivery
 * Additional Services columns); one modal listing every stop in route order, numbered sequentially
 * across the whole route rather than the strip's own Shipper/Stop N/Consignee labels.
 */
import { MarkerPin01 } from '@untitled-ui/icons-react/build/cjs';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommonModal from 'src/components/CommonModal';
import TabButton from 'src/components/TabButton';

import { IStopCard, StopDetailBlock } from '../QuoteDetail/CustomerPortalStopMetaStrip';

/** Sidebar nav values for the left rail (TabButton, vertical-sidebar mode). Only one section for
 * now — "Items per Stop" — but the rail keeps this modal consistent with the module's other
 * sidebar modals (QuoteDetailModalNew). */
const STOP_DETAIL_TAB = {
    PICKUPS_DROPOFFS: 'pickups-dropoffs',
} as const;

interface IProps {
    cards: IStopCard[];
    code?: string | null;
    handleClose: () => void;
    handlingUnitOptions?: any[];
}

const StopDetailsModal: FC<IProps> = ({ cards, code, handleClose, handlingUnitOptions }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<string>(STOP_DETAIL_TAB.PICKUPS_DROPOFFS);

    const sidebarTabs = [
        {
            value: STOP_DETAIL_TAB.PICKUPS_DROPOFFS,
            name: t('MyQuote.Pickups_And_Dropoffs'),
            icon: <MarkerPin01 className="vertical-secondary-tab-item-icon flex-none" />,
        },
    ];

    return (
        <CommonModal
            title={code ? `#${code}` : t('MyQuote.Customer_Portal_Stop')}
            handleClose={handleClose}
            size="max-w-[980px] h-[80vh]"
            modalClassName="flex flex-col"
            ModalWithSidebar={true}
            ContentLeft={
                <TabButton
                    activeClassName="tab-box-vertical-active"
                    isModalTabVerticalSidebar={true}
                    isVerticalBoxTab={true}
                    tabArray={sidebarTabs}
                    isActive={activeTab}
                    handleOnClick={(e: any) => setActiveTab(e.currentTarget.dataset.value)}
                />
            }
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={handleClose}
        >
            <div className="flex flex-1 overflow-auto">
                <div className="flex-1 px-4 py-4 overflow-auto custom-scrollbar-v2 scrollbar-hide">
                    {activeTab === STOP_DETAIL_TAB.PICKUPS_DROPOFFS && (
                        <div className="space-y-1.5">
                            <p className="text-sm font-medium text-grayLight900">{t('MyQuote.Stops_Details_Section')}</p>
                            <div className="rounded-xl border border-utilityGray200 bg-utilityGray300 p-3">
                                <div className="flex flex-col">
                                    {cards.map((card, idx) => (
                                        <React.Fragment key={card.position}>
                                            {idx > 0 && <div className="h-px bg-utilityGray200 mb-3 mt-6" />}
                                            <StopDetailBlock index={idx + 1} card={card} handlingUnitOptions={handlingUnitOptions} />
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </CommonModal>
    );
};

export default StopDetailsModal;
